<template>
  <div class="admin-subscribe-dashboard">
    <h2>구독 대시보드</h2>
    <div class="dashboard-box">
      <h3>구독 현황</h3>
      <div class="kpi-card-container">
        <div>
          <p class="count-title">활성 구독 수</p>
          <p class="count-text">
            <router-link to="/admin/product/list"
              ><span class="count-number">{{
                subscribeActiveCount.toLocaleString()
              }}</span>
              개</router-link
            >
          </p>
        </div>
        <div>
          <p class="count-title">결제 예정/대기 수</p>
          <p class="count-text">
            <router-link to="/admin/product/list"
              ><span class="count-number">{{
                subscribePendingCount.toLocaleString()
              }}</span>
              개</router-link
            >
          </p>
        </div>
        <div>
          <p class="count-title">연체</p>
          <p class="count-text">
            <router-link to=""
              ><span class="count-number">{{
                subscribePastDueCount.toLocaleString()
              }}</span>
              개</router-link
            >
          </p>
        </div>
        <div>
          <p class="count-title">7일 신규</p>
          <p class="count-text">
            <router-link to="/admin/product/deleted-list"
              ><span class="count-number">{{
                subscribe7DaysNewCount.toLocaleString()
              }}</span>
              개</router-link
            >
          </p>
        </div>
      </div>
      <div class="table-box">
        <h3>결제 대기 리스트</h3>
        <div ref="tableRef"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import "frappe-datatable/dist/frappe-datatable.min.css";
import DataTable from "frappe-datatable";
import {
  getDocs,
  collection,
  query,
  where,
  Timestamp,
  getDoc,
  doc,
} from "firebase/firestore";
import { onMounted, ref } from "vue";
import { db } from "@/lib/firebase";
import { formatDate, formatPaymentMethod } from "@/lib/utils";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const tableRef = ref(null);
const dataTable = ref(null);

const subscribePendingDatas = ref([]);
const subscribePastDueDatas = ref();
const subscribeActiveCount = ref(0);
const subscribePendingCount = ref(0);
const subscribePastDueCount = ref(0);
const subscribe7DaysNewCount = ref(0);

onMounted(() => {
  runLocked(async () => {
    const now = new Date();

    // ✅ 최근 7일 범위 (오늘 포함)
    const start7DaysAgoDate = Timestamp.fromDate(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 6, // 오늘 포함 7일
        0,
        0,
        0,
        0
      )
    );
    const endTodayDate = Timestamp.fromDate(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999
      )
    );

    // 병렬 조회
    const [
      subscribePendingQuerySnapshot,
      pastDueSnap,
      activeSnap,
      new7DaysSnap,
    ] = await Promise.all([
      getDocs(
        query(
          collection(db, "subscribePayments"),
          where("status", "==", "pending")
        )
      ),
      getDocs(
        query(
          collection(db, "userSubscribes"),
          where("status", "==", "past_due")
        )
      ),
      getDocs(
        query(collection(db, "userSubscribes"), where("status", "==", "active"))
      ),
      getDocs(
        query(
          collection(db, "userSubscribes"),
          where("createdAt", ">=", start7DaysAgoDate),
          where("createdAt", "<=", endTodayDate)
        )
      ),
    ]);

    // 원본 데이터 저장
    subscribePendingDatas.value = subscribePendingQuerySnapshot.docs.map(
      (docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })
    );

    subscribePastDueDatas.value = pastDueSnap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    // 카운트
    subscribeActiveCount.value = activeSnap.size;
    subscribePendingCount.value = subscribePendingDatas.value.length;
    subscribePastDueCount.value = pastDueSnap.size;
    subscribe7DaysNewCount.value = new7DaysSnap.size;

    // 테이블 데이터 생성 (회원 정보 병합)
    const pendingDatas = await Promise.all(
      subscribePendingQuerySnapshot.docs.map(async (d) => {
        const item = { id: d.id, ...d.data() };

        const userSnap = await getDoc(doc(db, "users", item.userId));
        item.userData = userSnap.exists() ? userSnap.data() : null;

        return [
          {
            content: item.dueAt || null,
            editable: false,
            format: (value) =>
              value?.toDate ? formatDate(value.toDate()) : "-",
          },
          {
            content: item.userData,
            editable: false,
            format: (value) => {
              if (!value) return "회원정보 없음";
              const digits = String(value.userPhone ?? "").replace(/\D/g, "");
              const last4 = digits.slice(-4);
              return `${value.userName || "이름없음"} (${last4 || "번호없음"})`;
            },
          },
          { content: item.subscribeId || "-", editable: false },
          {
            content: Number(item.amount || 0),
            editable: false,
            format: (value) => `${Number(value || 0).toLocaleString()}원`,
          },
          {
            content: item.method || "",
            editable: false,
            format: (value) => formatPaymentMethod(value),
          },
          {
            content: item.requestedAt || null,
            editable: false,
            format: (value) =>
              value?.toDate ? formatDate(value.toDate()) : "-",
          },
          { content: item.status || "-", editable: false },
          {
            content: item.id,
            editable: false,
            format: (value) =>
              `<div class="action-button-container">
                <button value="${value}">결제확정</button>
                <button value="${value}">상세보기</button>
              </div>`,
          },
        ];
      })
    );

    // 기존 테이블 제거 (있으면)
    if (dataTable.value) {
      try {
        dataTable.value.destroy();
      } catch (e) {
        console.warn("기존 DataTable destroy 실패(무시 가능):", e);
      }
      dataTable.value = null;
    }

    // DataTable 생성
    dataTable.value = new DataTable(tableRef.value, {
      columns: [
        {
          name: "납부기한",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "회원",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "플랜",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "금액",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "결제수단",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "요청일",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "상태",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "액션",
          editable: false,
          resizable: false,
          width: 192,
          align: "center",
        },
      ],
      data: pendingDatas,
      checkboxColumn: true,
      serialNoColumn: false,
      cellHeight: 64,
      inlineFilters: true,
    });
  }).catch((error) => {
    console.error("구독 대시보드 로딩 실패:", error);
    alert("구독 데이터를 불러오는 중 오류가 발생했습니다.");
  });
});
</script>

<style scoped lang="scss">
.admin-subscribe-dashboard {
  margin-top: 36px;

  > .dashboard-box {
    margin-top: 24px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
    padding: 24px;

    > .kpi-card-container {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      margin-top: 16px;

      > div {
        background-color: #efefef;
        border-radius: 8px;
        padding: 16px;

        > .count-title {
          font-size: 14px;
          font-weight: 500;
        }

        > .count-text {
          margin-top: 4px;
          display: flex;
          align-items: center;
          font-size: 14px;

          > a {
            > .count-number {
              color: #007bff;
              font-size: 18px;
              font-weight: 700;
              margin-right: 4px;
              text-decoration: underline;
            }
          }
        }
      }
    }

    > .table-box {
      margin-top: 24px;

      > div {
        margin-top: 16px;
        width: 100%;
        height: fit-content;
      }
    }
  }
}
</style>
